import React from 'react';
import { createBrowserRouter, createHashRouter, Navigate, useLocation, useParams } from 'react-router-dom';
import { ENV } from '@/config/env/env.read';
import RequireSession from '@/router/Require.Session';
import SessionSplash from '@/router/Session.Splash';
import Login from '@/pages/auth/login/Login';
import Reset from '@/pages/auth/reset/Reset';
import EmailChange from '@/pages/auth/email-change/Email.Change';
import NotFound from '@/pages/error/Not.Found';
import CompanyGate from '@/components/company/Company.Gate';

const page = (load: () => Promise<{ default: React.ComponentType }>) =>
    async () => ({ Component: (await load()).default });

const Overview = page(() => import('@/pages/overview/Overview'));
const Cloud = page(() => import('@/pages/cloud/Cloud'));
const SectionPage = page(() => import('@/pages/section/Section.Page'));
const Traffic = page(() => import('@/pages/traffic/Traffic'));
const TrafficOrigin = page(() => import('@/pages/traffic/origin/Origin'));
const TrafficErrors = page(() => import('@/pages/traffic/errors/Errors'));
const TrafficLive = page(() => import('@/pages/traffic/live/Live'));
const Servers = page(() => import('@/pages/servers/Servers'));
const Services = page(() => import('@/pages/begateway/services/Services'));
const Keys = page(() => import('@/pages/begateway/keys/Keys'));
const Routes = page(() => import('@/pages/begateway/routes/Routes'));
const Cors = page(() => import('@/pages/begateway/cors/Cors'));
const Access = page(() => import('@/pages/begateway/access/Access'));
const Bans = page(() => import('@/pages/begateway/bans/Bans'));
const Diagnose = page(() => import('@/pages/begateway/diagnose/Diagnose'));
const Hygiene = page(() => import('@/pages/begateway/hygiene/Hygiene'));
const Audit = page(() => import('@/pages/begateway/audit/Audit'));
const Metrics = page(() => import('@/pages/begateway/metrics/Metrics'));
const AuthServices = page(() => import('@/pages/beauth/services/Services'));
const AuthGroups = page(() => import('@/pages/beauth/groups/Groups'));
const AuthClients = page(() => import('@/pages/beauth/clients/Clients'));
const AuthPeople = page(() => import('@/pages/beauth/people/People'));
const AuthPerson = page(() => import('@/pages/beauth/people/Person'));
const AuthStations = page(() => import('@/pages/beauth/stations/Stations'));
const AuthStation = page(() => import('@/pages/beauth/stations/Station'));
const AuthEnrollments = page(() => import('@/pages/beauth/enrollments/Enrollments'));
const AuthGrants = page(() => import('@/pages/beauth/grants/Grants'));
const AuthDevices = page(() => import('@/pages/beauth/devices/Devices'));
const AuthAuthorizations = page(() => import('@/pages/beauth/authorizations/Authorizations'));
const AuthSessions = page(() => import('@/pages/beauth/sessions/Sessions'));
const AuthAccessLog = page(() => import('@/pages/beauth/log/Log'));
const AuthScim = page(() => import('@/pages/beauth/scim/Scim'));
const AuthPasskeys = page(() => import('@/pages/beauth/passkeys/Passkeys'));
const AuthFederation = page(() => import('@/pages/beauth/federation/Federation'));
const AuthOrganizations = page(() => import('@/pages/beauth/organizations/Organizations'));
const AuthSsf = page(() => import('@/pages/beauth/ssf/Ssf'));
const AuthTokens = page(() => import('@/pages/beauth/tokens/Tokens'));
const AuthAudit = page(() => import('@/pages/beauth/audit/Audit'));
const Profile = page(() => import('@/pages/profile/Profile'));
const Settings = page(() => import('@/pages/settings/Settings'));
const WorkBoard = page(() => import('@/pages/work/board/Board'));
const WorkProject = page(() => import('@/pages/work/project/Project'));
const WorkTask = page(() => import('@/pages/work/task/Task'));
const WorkSchema = page(() => import('@/pages/work/schema/Schema'));
const WorkCalendar = page(() => import('@/pages/work/calendar/Calendar'));
const Quotes = page(() => import('@/pages/commercial/quotes/Quotes'));
const Quote = page(() => import('@/pages/commercial/quotes/Quote'));
const Catalog = page(() => import('@/pages/commercial/catalog/Catalog'));
const CatalogSheet = page(() => import('@/pages/commercial/catalog/Catalog.Sheet'));
const Invoices = page(() => import('@/pages/commercial/invoices/Invoices'));
const Invoice = page(() => import('@/pages/commercial/invoices/Invoice'));
const Expenses = page(() => import('@/pages/commercial/expenses/Expenses'));
const RecurringPage = page(() => import('@/pages/commercial/recurring/Recurring'));
const Clients = page(() => import('@/pages/clients/Clients'));
const Conversations = page(() => import('@/pages/conversations/email/Conversations'));
const Chat = page(() => import('@/pages/conversations/chat/Chat'));
const Client = page(() => import('@/pages/clients/Client'));

const Moved: React.FC<{ to: string }> = ({ to }) => {
    const { id } = useParams();
    return <Navigate to={id ? `${to}/${id}` : to} replace />;
};

const MovedBranch: React.FC<{ to: string }> = ({ to }) => {
    const location = useLocation();
    const params = useParams();

    const rest = params['*'] ?? '';

    return <Navigate to={`${to}${rest ? `/${rest}` : ''}${location.search}`} replace />;
};

const MovedTask: React.FC = () => {
    const { id } = useParams();
    return <Navigate to={id ? `/work/task/${id}` : '/work/board'} replace />;
};

const createRouter = ENV.routerMode === 'hash' ? createHashRouter : createBrowserRouter;

export const router = createRouter([
    { path: '/auth/login', element: <Login /> },
    { path: '/auth/verify', element: <Navigate to="/auth/login" replace /> },
    { path: '/auth/recovery', element: <Navigate to="/auth/reset" replace /> },
    { path: '/auth/reset', element: <Reset /> },
    { path: '/auth/email-change', element: <EmailChange /> },
    { path: '/login', element: <Navigate to="/auth/login" replace /> },
    {
        path: '/',
        element: <RequireSession />,
        hydrateFallbackElement: <SessionSplash />,
        children: [
            { index: true, element: <Navigate to="/overview" replace /> },
            {
                element: <CompanyGate />,
                children: [
                    { path: 'overview', lazy: Overview },
                    { path: 'clients', lazy: Clients },
                    { path: 'clients/:id', lazy: Client },
                    { path: 'conversations', element: <Navigate to="/conversations/mail" replace /> },
                    { path: 'conversations/mail/contacts', lazy: Conversations },
                    { path: 'conversations/mail/drafts', lazy: Conversations },
                    { path: 'conversations/mail/:id?', lazy: Conversations },
                    { path: 'conversations/chat/:channelId?', lazy: Chat },
                    { path: 'conversations/contacts', element: <Navigate to="/conversations/mail/contacts" replace /> },
                    { path: 'conversations/drafts', element: <Navigate to="/conversations/mail/drafts" replace /> },
                    { path: 'conversations/:id', element: <Moved to="/conversations/mail" /> },
                    { path: 'commercial', element: <Navigate to="/commercial/quotes" replace /> },
                    { path: 'commercial/quotes', lazy: Quotes },
                    { path: 'commercial/quotes/:id', lazy: Quote },
                    { path: 'commercial/clients/:id?', element: <Moved to="/clients" /> },
                    { path: 'commercial/invoices', lazy: Invoices },
                    { path: 'commercial/invoices/:id', lazy: Invoice },
                    { path: 'commercial/expenses/:id?', lazy: Expenses },
                    { path: 'commercial/recurring', lazy: RecurringPage },
                    { path: 'commercial/deadlines', element: <Moved to="/commercial/invoices" /> },
                    { path: 'commercial/catalog/pdf', lazy: CatalogSheet },
                    { path: 'commercial/catalog/:id?', lazy: Catalog },
                    { path: 'commercial/catalog/pacchetto/:bundleId', lazy: Catalog },
                ],
            },
            { path: 'cloud', lazy: Cloud },
            { path: 'traffic', lazy: Traffic },
            { path: 'traffic/origin', lazy: TrafficOrigin },
            { path: 'traffic/errors', lazy: TrafficErrors },
            { path: 'traffic/live', lazy: TrafficLive },
            { path: 'servers/:server?/:tab?/:item?', lazy: Servers },
            { path: 'machines', element: <MovedBranch to="/servers" /> },
            { path: 'machines/*', element: <MovedBranch to="/servers" /> },
            { path: 'traffic/routes', element: <Navigate to="/begateway/routes?ordina=traffic" replace /> },
            { path: 'begateway', element: <Navigate to="/begateway/services" replace /> },
            { path: 'begateway/services/:serviceId?', lazy: Services },
            { path: 'begateway/keys/:id?', lazy: Keys },
            { path: 'begateway/routes/:kind?/:name?', lazy: Routes },
            { path: 'begateway/cors/:id?', lazy: Cors },
            { path: 'begateway/access', element: <Navigate to="/begateway/access/rules" replace /> },
            { path: 'begateway/access/rules/:id?', lazy: Access },
            { path: 'begateway/access/bans/:key?', lazy: Bans },
            { path: 'begateway/bans', element: <Navigate to="/begateway/access/bans" replace /> },
            { path: 'begateway/diagnostics', element: <Navigate to="/begateway/diagnostics/request" replace /> },
            { path: 'begateway/diagnostics/request', lazy: Diagnose },
            { path: 'begateway/diagnostics/hygiene/:id?', lazy: Hygiene },
            { path: 'begateway/diagnostics/metrics/:name?', lazy: Metrics },
            { path: 'begateway/audit', lazy: Audit },
            { path: 'begateway/diagnose', element: <Navigate to="/begateway/diagnostics/request" replace /> },
            { path: 'begateway/hygiene', element: <Navigate to="/begateway/diagnostics/hygiene" replace /> },
            { path: 'begateway/metrics', element: <Navigate to="/begateway/diagnostics/metrics" replace /> },

            { path: 'beauth', element: <Navigate to="/beauth/services/config" replace /> },
            { path: 'beauth/services', element: <Navigate to="/beauth/services/config" replace /> },
            { path: 'beauth/services/config/:serviceId?', lazy: AuthServices },
            { path: 'beauth/services/groups/:group?', lazy: AuthGroups },
            { path: 'beauth/services/clients/:clientId?', lazy: AuthClients },
            { path: 'beauth/services/security', element: <Navigate to="/beauth/services/config" replace /> },
            { path: 'beauth/services/tokens', element: <Navigate to="/beauth/services/config" replace /> },
            { path: 'beauth/services/branding', element: <Navigate to="/beauth/services/config" replace /> },

            { path: 'beauth/users', element: <Navigate to="/beauth/users/people" replace /> },
            { path: 'beauth/users/people', lazy: AuthPeople },
            { path: 'beauth/users/people/:id', lazy: AuthPerson },
            { path: 'beauth/users/stations', lazy: AuthStations },
            { path: 'beauth/users/stations/:id', lazy: AuthStation },
            { path: 'beauth/users/enrollments/:key?', lazy: AuthEnrollments },
            { path: 'beauth/users/grants/:key?', lazy: AuthGrants },

            { path: 'beauth/access', element: <Navigate to="/beauth/access/devices" replace /> },
            { path: 'beauth/access/devices/:id?', lazy: AuthDevices },
            { path: 'beauth/access/authorization/:id?', lazy: AuthAuthorizations },
            { path: 'beauth/access/sessions/:id?', lazy: AuthSessions },
            { path: 'beauth/access/log/:id?', lazy: AuthAccessLog },
            { path: 'beauth/devices', element: <Navigate to="/beauth/access/devices" replace /> },
            { path: 'beauth/devices/known/:id?', element: <Moved to="/beauth/access/devices" /> },
            { path: 'beauth/devices/authorization/:id?', element: <Moved to="/beauth/access/authorization" /> },
            { path: 'beauth/sessions/:id?', element: <Moved to="/beauth/access/sessions" /> },
            { path: 'beauth/access-log', element: <Navigate to="/beauth/access/log" replace /> },

            { path: 'beauth/identity', element: <Navigate to="/beauth/identity/passkeys" replace /> },
            { path: 'beauth/identity/passkeys/:id?', lazy: AuthPasskeys },
            { path: 'beauth/identity/federation/:id?', lazy: AuthFederation },
            { path: 'beauth/identity/scim/:id?', lazy: AuthScim },
            { path: 'beauth/passkeys', element: <Navigate to="/beauth/identity/passkeys" replace /> },
            { path: 'beauth/federation', element: <Navigate to="/beauth/identity/federation" replace /> },
            { path: 'beauth/scim/:id?', element: <Moved to="/beauth/identity/scim" /> },

            { path: 'beauth/organizations/:orgId?', lazy: AuthOrganizations },

            { path: 'beauth/ssf/:id?', lazy: AuthSsf },
            { path: 'beauth/tokens/:id?', lazy: AuthTokens },

            { path: 'beauth/audit/:id?', lazy: AuthAudit },
            { path: 'work', element: <Navigate to="/work/board" replace /> },
            { path: 'work/board', lazy: WorkBoard },
            { path: 'work/calendar/:id?', lazy: WorkCalendar },
            { path: 'work/project/:id', lazy: WorkProject },
            { path: 'work/task/:id', lazy: WorkTask },
            { path: 'work/task/:id/schema/:noteId', lazy: WorkSchema },
            { path: 'work/board/:id', element: <MovedTask /> },


            { path: 'settings', element: <Navigate to="/settings/company" replace /> },
            { path: 'settings/:tab', lazy: Settings },
            { path: 'profile', element: <Navigate to="/profile/account" replace /> },
            { path: 'profile/activity', element: <Navigate to="/profile/devices" replace /> },
            { path: 'profile/services', element: <Navigate to="/profile/security" replace /> },
            { path: 'profile/:tab', lazy: Profile },

            { path: 'auth/*', element: <MovedBranch to="/beauth" /> },
            { path: 'gateway/*', element: <MovedBranch to="/begateway" /> },

            { path: '*', lazy: SectionPage },
        ],
    },
    { path: '/404', element: <NotFound /> },
]);
